import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

type ThemeMode = "light" | "dark";

interface ThemeContextType {
  theme: ThemeMode;
  setTheme: (theme: ThemeMode) => void;
  toggleTheme: () => void;
  isDark: boolean;
}

/**
 * Antes do login o tema vem do cookie (escolha explícita feita neste navegador) ou, sem ele, do
 * tema do sistema. Depois do login as preferências do usuário vencem: o UserPreferencesContext
 * chama setTheme, que também grava o cookie para o próximo carregamento já abrir certo.
 * O script inline do index.html lê o mesmo cookie para não piscar o tema errado.
 */
const THEME_COOKIE = "7days-theme";
const THEME_COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

let themeTransitionTimer = 0;

function applyThemeToDom(theme: ThemeMode, suppressTransition = false) {
  if (typeof document === "undefined") {
    return;
  }

  const root = document.documentElement;

  // Suprime transições por 1 frame para eliminar o flash/flicker na troca.
  if (suppressTransition) {
    root.classList.add("theme-switching");
  }

  const isDark = theme === "dark";
  root.classList.toggle("dark", isDark);
  root.setAttribute("data-theme", theme);
  document.body.classList.toggle("dark", isDark);
  document.body.setAttribute("data-theme", theme);

  if (suppressTransition) {
    // Mantém a classe durante o crossfade e remove ao terminar (~350ms).
    window.clearTimeout(themeTransitionTimer);
    themeTransitionTimer = window.setTimeout(() => {
      root.classList.remove("theme-switching");
    }, 400);
  }
}

function persistTheme(theme: ThemeMode) {
  if (typeof document === "undefined") {
    return;
  }
  document.cookie = `${THEME_COOKIE}=${theme}; Path=/; Max-Age=${THEME_COOKIE_MAX_AGE}; SameSite=Lax`;
}

function readCookieTheme(): ThemeMode | null {
  if (typeof document === "undefined") {
    return null;
  }
  const match = document.cookie.match(new RegExp(`(?:^|; )${THEME_COOKIE}=(dark|light)(?:;|$)`));
  return match ? (match[1] as ThemeMode) : null;
}

const SYSTEM_DARK_QUERY = "(prefers-color-scheme: dark)";

function readSystemTheme(): ThemeMode {
  if (typeof window === "undefined" || !window.matchMedia) {
    return "light";
  }
  return window.matchMedia(SYSTEM_DARK_QUERY).matches ? "dark" : "light";
}

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theme, setThemeState] = useState<ThemeMode>("light");

  useEffect(() => {
    const cookieTheme = readCookieTheme();
    const initialTheme = cookieTheme ?? readSystemTheme();
    setThemeState(initialTheme);
    applyThemeToDom(initialTheme);

    // Sem escolha gravada, acompanha o sistema até alguém escolher (ou as preferências chegarem).
    if (cookieTheme || typeof window === "undefined" || !window.matchMedia) {
      return;
    }
    const media = window.matchMedia(SYSTEM_DARK_QUERY);
    const followSystem = (event: MediaQueryListEvent) => {
      if (readCookieTheme()) return;
      const next: ThemeMode = event.matches ? "dark" : "light";
      setThemeState(next);
      applyThemeToDom(next, true);
    };
    media.addEventListener("change", followSystem);
    return () => media.removeEventListener("change", followSystem);
  }, []);

  const setTheme = useCallback((nextTheme: ThemeMode) => {
    setThemeState(nextTheme);
    applyThemeToDom(nextTheme, true);
    persistTheme(nextTheme);
  }, []);

  const toggleTheme = useCallback(() => {
    if (typeof document !== "undefined") {
      const nextTheme: ThemeMode = document.documentElement.classList.contains("dark") ? "light" : "dark";
      setTheme(nextTheme);
      return;
    }

    setTheme(theme === "dark" ? "light" : "dark");
  }, [theme, setTheme]);

  const value = useMemo(
    () => ({
      theme,
      setTheme,
      toggleTheme,
      isDark: theme === "dark",
    }),
    [theme, setTheme, toggleTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => {
  const context = useContext(ThemeContext);

  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }

  return context;
};
